import { createApi } from '@reduxjs/toolkit/query/react';
import {
  PromotionsResponse,
  PromotionDetailsResponse,
  GetPromotionsParams,
  ReturnPromotionRequest,
  RejectPromotionRequest,
  ApprovePromotionRequest,
} from '../../types/promotions';
import {
  PromotionDashboardResponse,
  GetPromotionsDashboardParams,
  PromotionsStatisticsResponse,
  PromotionDashboardDayDetailsResponse,
  GetPromotionsDashboardDayParams,
} from '../../types/promotionsReports';
import {
  PromotionDefaultContentResponse,
  GetPromotionDefaultContentsParams,
  CreatePromotionDefaultContentRequest,
  UpdatePromotionDefaultContentRequest,
  DocumentUploadResponse,
} from '../../types/promotionDefaultContents';
import { baseQueryWithReauth } from './baseQuery';

export const promotionsService = createApi({
  reducerPath: 'promotionsService',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['PROMOTIONS', 'PROMOTIONS_DASHBOARD', 'PROMOTIONS_STATISTICS', 'PROMOTION_DEFAULT_CONTENTS'],
  endpoints: (builder) => ({
    getPromotionsStatistics: builder.query<PromotionsStatisticsResponse, void>({
      query: () => ({
        url: '/marketplace-service/v1/operator/promotions/statistics',
        method: 'GET',
      }),
      providesTags: ['PROMOTIONS_STATISTICS'],
    }),

    getPromotionsDashboard: builder.query<PromotionDashboardResponse, GetPromotionsDashboardParams>({
      query: (params) => ({
        url: '/marketplace-service/v1/operator/promotions/dashboard',
        method: 'GET',
        params: params ? {
          fromDate: params.fromDate,
          toDate: params.toDate,
        } : undefined,
      }),
      providesTags: ['PROMOTIONS_DASHBOARD'],
    }),

    getPromotionsDashboardDay: builder.query<PromotionDashboardDayDetailsResponse, GetPromotionsDashboardDayParams>({
      query: (params) => ({
        url: '/marketplace-service/v1/operator/promotions/dashboard/day',
        method: 'GET',
        params: {
          marketplaceScopeCode: params.marketplaceScopeCode,
          promotionTypeCode: params.promotionTypeCode,
          day: params.day,
        },
      }),
      providesTags: ['PROMOTIONS_DASHBOARD'],
    }),

    getPromotions: builder.query<PromotionsResponse, GetPromotionsParams | void>({
      query: (params) => ({
        url: '/marketplace-service/v1/operator/promotions',
        method: 'GET',
        params: params
          ? {
              page: params.page,
              limit: params.limit,
              size: params.size ?? params.limit,
              search: params.search || undefined,
              status: params.status || undefined,
              marketplaceScopeCode: params.marketplaceScopeCode || undefined,
              promotionTypeCode: params.promotionTypeCode || undefined,
            }
          : undefined,
      }),
      providesTags: ['PROMOTIONS'],
    }),

    getPromotionDetails: builder.query<PromotionDetailsResponse, string>({
      query: (id) => ({
        url: `/marketplace-service/v1/operator/promotions/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'PROMOTIONS', id }],
    }),

    approvePromotion: builder.mutation<
      { payload?: any; success?: boolean; message?: string },
      string | ApprovePromotionRequest
    >({
      query: (arg) => {
        const id = typeof arg === 'string' ? arg : arg.id;
        return {
          url: `/marketplace-service/v1/operator/promotions/${id}/approve`,
          method: 'PUT',
        };
      },
      invalidatesTags: ['PROMOTIONS'],
    }),

    rejectPromotion: builder.mutation<
      { payload?: any; success?: boolean; message?: string },
      string | RejectPromotionRequest
    >({
      query: (arg) => {
        const id = typeof arg === 'string' ? arg : arg.id;
        const body = typeof arg === 'string' ? undefined : arg.body;
        return {
          url: `/marketplace-service/v1/operator/promotions/${id}/reject`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['PROMOTIONS'],
    }),

    returnPromotion: builder.mutation<
      { payload?: any; success?: boolean; message?: string },
      ReturnPromotionRequest
    >({
      query: ({ id, body }) => ({
        url: `/marketplace-service/v1/operator/promotions/${id}/return`,
        method: 'PUT',
        body: {
          message: body.message,
        },
      }),
      invalidatesTags: ['PROMOTIONS'],
    }),

    getPromotionDefaultContents: builder.query<PromotionDefaultContentResponse, GetPromotionDefaultContentsParams>({
      query: (params) => ({
        url: '/marketplace-service/v1/operator/promotion-default-contents',
        method: 'GET',
        params: {
          marketplaceScopeCode: params.marketplaceScopeCode,
        },
      }),
      providesTags: ['PROMOTION_DEFAULT_CONTENTS'],
    }),

    createPromotionDefaultContent: builder.mutation<any, CreatePromotionDefaultContentRequest>({
      query: (body) => ({
        url: '/marketplace-service/v1/operator/promotion-default-contents',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PROMOTION_DEFAULT_CONTENTS'],
    }),

    updatePromotionDefaultContent: builder.mutation<any, UpdatePromotionDefaultContentRequest>({
      query: ({ id, body }) => ({
        url: `/marketplace-service/v1/operator/promotion-default-contents/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['PROMOTION_DEFAULT_CONTENTS'],
    }),

    deletePromotionDefaultContent: builder.mutation<any, string>({
      query: (id) => ({
        url: `/marketplace-service/v1/operator/promotion-default-contents/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PROMOTION_DEFAULT_CONTENTS'],
    }),

    uploadPromotionDefaultContentDocuments: builder.mutation<DocumentUploadResponse, FormData>({
      query: (body) => ({
        url: '/marketplace-service/v1/operator/promotion-default-contents/documents',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetPromotionsStatisticsQuery,
  useGetPromotionsDashboardQuery,
  useGetPromotionsDashboardDayQuery,
  useLazyGetPromotionsDashboardDayQuery,
  useGetPromotionsQuery,
  useGetPromotionDetailsQuery,
  useApprovePromotionMutation,
  useRejectPromotionMutation,
  useReturnPromotionMutation,
  useGetPromotionDefaultContentsQuery,
  useCreatePromotionDefaultContentMutation,
  useUpdatePromotionDefaultContentMutation,
  useDeletePromotionDefaultContentMutation,
  useUploadPromotionDefaultContentDocumentsMutation,
} = promotionsService;
